/**
 * Utilities for parsing poker hand range notation
 */

import { HAND_METADATA } from '@/data/handMetadata';

// Set of all valid hand names for quick lookup
const VALID_HANDS = new Set(Object.keys(HAND_METADATA));

// Valid rank characters in descending order (A is highest)
const RANKS = ['A', 'K', 'Q', 'J', 'T', '9', '8', '7', '6', '5', '4', '3', '2'];
const RANK_SET = new Set(RANKS);

/**
 * Get rank index (0 = A, 12 = 2)
 */
function getRankIndex(rank: string): number {
  return RANKS.indexOf(rank);
}

/**
 * Expand a pair range (e.g., "44+" -> ["44", ..., "AA"] or "77-TT" -> ["77", ..., "TT"])
 * @param startRank - The starting rank (lower bound)
 * @param endRank - The ending rank (upper bound), or undefined for "+" notation (defaults to A)
 */
function expandPairRange(startRank: string, endRank?: string): string[] {
  const startIndex = getRankIndex(startRank);
  const endIndex = endRank ? getRankIndex(endRank) : 0; // 0 = Ace (highest)

  if (startIndex === -1 || endIndex === -1) return [];
  if (endIndex > startIndex) return []; // End must be >= start (higher or equal rank)

  const results: string[] = [];
  // Go from startIndex down to endIndex (lower index = higher rank)
  for (let i = startIndex; i >= endIndex; i--) {
    const pair = RANKS[i] + RANKS[i];
    if (VALID_HANDS.has(pair)) {
      results.push(pair);
    }
  }
  return results;
}

/**
 * Expand a non-pair range (e.g., "AJs+" or "ATs-AQs" or "54s-87s" or "86s-KJs")
 *
 * There are two types of ranges:
 * 1. Sliding ranges: both cards slide up maintaining gap (connectors/gappers like 76s+, 86s-KJs)
 * 2. Fixed high card ranges: high card stays fixed, kicker improves (like K7s+, ATs-AQs)
 *
 * For dash ranges, we detect which type based on whether both hands have the same gap.
 * For plus ranges, connectors (gap=1) slide, non-connectors keep high card fixed.
 *
 * @param startRank1 - High card of starting hand
 * @param startRank2 - Low card (kicker) of starting hand
 * @param endRank1 - High card of ending hand, or undefined for "+" notation
 * @param endRank2 - Low card of ending hand, or undefined for "+" notation
 * @param suffix - 's' or 'o'
 */
function expandNonPairRange(
  startRank1: string,
  startRank2: string,
  endRank1: string | undefined,
  endRank2: string | undefined,
  suffix: 's' | 'o',
): string[] {
  const startIdx1 = getRankIndex(startRank1);
  const startIdx2 = getRankIndex(startRank2);
  if (startIdx1 === -1 || startIdx2 === -1) return [];

  // Gap between the two cards (e.g., AK has gap 1, A5 has gap 8)
  const startGap = startIdx2 - startIdx1;
  if (startGap <= 0) return []; // Invalid: rank1 should be higher than rank2

  const results: string[] = [];

  // Determine if this is a sliding range or fixed high card range
  let isSliding = false;

  if (endRank1 !== undefined && endRank2 !== undefined) {
    // Dash range: check if both hands have the same gap (sliding) or same high card (fixed)
    const endIdx1 = getRankIndex(endRank1);
    const endIdx2 = getRankIndex(endRank2);
    if (endIdx1 === -1 || endIdx2 === -1) return [];

    const endGap = endIdx2 - endIdx1;

    if (startGap === endGap && startRank1 !== endRank1) {
      // Same gap, different high cards = sliding range (e.g., 86s-KJs)
      isSliding = true;
    }
    // Otherwise it's a fixed high card range (e.g., ATs-AQs where high card is A)
  } else {
    // Plus range: connectors slide, non-connectors keep high card fixed
    isSliding = startGap === 1;
  }

  if (isSliding) {
    // Sliding range: both cards move up maintaining gap
    const endIdx1 = endRank1 ? getRankIndex(endRank1) : 0; // Default to A for "+"

    for (let newIdx1 = startIdx1; newIdx1 >= endIdx1; newIdx1--) {
      const newIdx2 = newIdx1 + startGap;
      if (newIdx2 >= RANKS.length) break; // kicker would go below 2

      const hand = RANKS[newIdx1] + RANKS[newIdx2] + suffix;
      if (VALID_HANDS.has(hand)) {
        results.push(hand);
      }
    }
  } else {
    // Fixed high card range: keep high card, move kicker up
    const defaultEndIdx = startIdx1 + 1; // Default: one below the high card
    const endIdx2 = endRank2 ? getRankIndex(endRank2) : defaultEndIdx;

    for (let kickerIdx = startIdx2; kickerIdx >= endIdx2; kickerIdx--) {
      const hand = startRank1 + RANKS[kickerIdx] + suffix;
      if (VALID_HANDS.has(hand)) {
        results.push(hand);
      }
    }
  }

  return results;
}

/**
 * Expand a wildcard where the rank is the high card (e.g., "8*" = 87, 86, 85, 84, 83, 82)
 * Returns all hands where the given rank is the high card (both suited and offsuit)
 */
function expandHighCardWildcard(rank: string): string[] {
  const rankIdx = getRankIndex(rank);
  if (rankIdx === -1 || rankIdx >= RANKS.length - 1) return []; // No cards below 2

  const results: string[] = [];

  // All kickers below this rank
  for (let kickerIdx = rankIdx + 1; kickerIdx < RANKS.length; kickerIdx++) {
    const suited = rank + RANKS[kickerIdx] + 's';
    const offsuit = rank + RANKS[kickerIdx] + 'o';

    if (VALID_HANDS.has(suited)) results.push(suited);
    if (VALID_HANDS.has(offsuit)) results.push(offsuit);
  }

  return results;
}

/**
 * Expand a wildcard where the rank is the kicker (e.g., "*8" = A8, K8, Q8, J8, T8, 98)
 * Returns all hands where the given rank is the kicker (both suited and offsuit)
 */
function expandKickerWildcard(rank: string): string[] {
  const rankIdx = getRankIndex(rank);
  if (rankIdx === -1 || rankIdx === 0) return []; // No cards above A

  const results: string[] = [];

  // All high cards above this rank
  for (let highIdx = 0; highIdx < rankIdx; highIdx++) {
    const suited = RANKS[highIdx] + rank + 's';
    const offsuit = RANKS[highIdx] + rank + 'o';

    if (VALID_HANDS.has(suited)) results.push(suited);
    if (VALID_HANDS.has(offsuit)) results.push(offsuit);
  }

  return results;
}

/**
 * Parse a hand string into its components
 * Returns: { ranks: string (uppercase), suffix: 's' | 'o' | null }
 */
function parseHandPart(hand: string): { ranks: string; suffix: 's' | 'o' | null } | null {
  if (!hand) return null;

  const normalized = hand.replace(/10/g, 'T');
  const lastChar = normalized.slice(-1).toLowerCase();
  const hasSuffix = lastChar === 's' || lastChar === 'o';

  if (hasSuffix) {
    return {
      ranks: normalized.slice(0, -1).toUpperCase(),
      suffix: lastChar as 's' | 'o',
    };
  }

  return {
    ranks: normalized.toUpperCase(),
    suffix: null,
  };
}

/**
 * Expand a hand expression which may include range notation
 * Handles:
 * - Single hands: "AA", "AKs", "AKo"
 * - Unprefixed hands: "AK" -> "AKs" + "AKo"
 * - Plus ranges: "44+", "AJs+", "AJo+", "AJ+"
 * - Dash ranges: "77-TT", "ATs-AQs", "54s-87s", "AT-AQ"
 * - Wildcards: "8*" (8 as high card), "*8" (8 as kicker)
 *
 * Returns an array of valid hand names
 */
function expandHandExpression(hand: string): string[] {
  const trimmed = hand.trim();
  if (!trimmed) return [];

  // Handle wildcards (8*, *8)
  if (trimmed.includes('*')) {
    const normalized = trimmed.replace(/10/g, 'T').toUpperCase();

    // 8* pattern: rank as high card with any kicker
    if (normalized.length === 2 && normalized[1] === '*' && RANK_SET.has(normalized[0])) {
      return expandHighCardWildcard(normalized[0]);
    }

    // *8 pattern: any high card with rank as kicker
    if (normalized.length === 2 && normalized[0] === '*' && RANK_SET.has(normalized[1])) {
      return expandKickerWildcard(normalized[1]);
    }

    return []; // Other wildcard patterns not supported
  }

  // Check for dash range notation (e.g., "77-TT", "ATs-AQs")
  if (trimmed.includes('-')) {
    const [startPart, endPart] = trimmed.split('-');
    const start = parseHandPart(startPart.trim());
    const end = parseHandPart(endPart.trim());

    if (!start || !end) return [];

    // Both parts must have same suffix (or both no suffix)
    if (start.suffix !== end.suffix) return [];

    const suffix = start.suffix;

    // Handle pair dash range (e.g., "77-TT")
    if (
      start.ranks.length === 2 &&
      start.ranks[0] === start.ranks[1] &&
      end.ranks.length === 2 &&
      end.ranks[0] === end.ranks[1]
    ) {
      return expandPairRange(start.ranks[0], end.ranks[0]);
    }

    // Handle non-pair dash range with suffix (e.g., "ATs-AQs", "54s-87s")
    if (suffix && start.ranks.length === 2 && end.ranks.length === 2) {
      const [startRank1, startRank2] = start.ranks.split('');
      const [endRank1, endRank2] = end.ranks.split('');
      return expandNonPairRange(startRank1, startRank2, endRank1, endRank2, suffix);
    }

    // Handle unprefixed dash range (e.g., "AT-AQ")
    if (!suffix && start.ranks.length === 2 && end.ranks.length === 2) {
      const [startRank1, startRank2] = start.ranks.split('');
      const [endRank1, endRank2] = end.ranks.split('');
      const suited = expandNonPairRange(startRank1, startRank2, endRank1, endRank2, 's');
      const offsuit = expandNonPairRange(startRank1, startRank2, endRank1, endRank2, 'o');
      return [...suited, ...offsuit];
    }

    return [];
  }

  // Check for plus range notation
  const hasPlus = trimmed.endsWith('+');
  const withoutPlus = hasPlus ? trimmed.slice(0, -1) : trimmed;

  const parsed = parseHandPart(withoutPlus);
  if (!parsed) return [];

  const { ranks, suffix } = parsed;

  if (suffix) {
    // Has suffix (s/o)
    if (hasPlus && ranks.length === 2) {
      const [rank1, rank2] = ranks.split('');
      return expandNonPairRange(rank1, rank2, undefined, undefined, suffix);
    }

    const normalized = ranks + suffix;
    if (VALID_HANDS.has(normalized)) {
      return [normalized];
    }
    return [];
  }

  // No suffix - could be pair or unprefixed non-pair
  // Check for pair (e.g., "AA", "KK", "44+")
  if (ranks.length === 2 && ranks[0] === ranks[1] && RANK_SET.has(ranks[0])) {
    if (hasPlus) {
      return expandPairRange(ranks[0]);
    }
    if (VALID_HANDS.has(ranks)) {
      return [ranks];
    }
    return [];
  }

  // Unprefixed non-pair (e.g., "AK" -> "AKs" + "AKo", or "AK+" -> both plus ranges)
  if (ranks.length === 2) {
    const [rank1, rank2] = ranks.split('');
    if (RANK_SET.has(rank1) && RANK_SET.has(rank2) && rank1 !== rank2) {
      if (hasPlus) {
        const suitedRange = expandNonPairRange(rank1, rank2, undefined, undefined, 's');
        const offsuitRange = expandNonPairRange(rank1, rank2, undefined, undefined, 'o');
        return [...suitedRange, ...offsuitRange];
      }

      // Single unprefixed hand expands to both suited and offsuit
      const suited = ranks + 's';
      const offsuit = ranks + 'o';
      const results: string[] = [];

      if (VALID_HANDS.has(suited)) results.push(suited);
      if (VALID_HANDS.has(offsuit)) results.push(offsuit);

      return results;
    }
  }

  return [];
}

/**
 * Parse a comma-separated list of hand expressions
 * Returns array of valid hand names, ignoring invalid ones
 *
 * Supports:
 * - Single hands: "AA", "AKs", "AKo"
 * - Unprefixed hands: "AK" -> "AKs" + "AKo"
 * - Plus ranges: "44+", "AJs+", "AJo+", "AJ+"
 *
 * @example
 * parseHandList("AA, KK, AKs, invalid") // ["AA", "KK", "AKs"]
 * parseHandList("AQ") // ["AQs", "AQo"]
 * parseHandList("44+") // ["44", "55", ..., "AA"]
 * parseHandList("AJs+") // ["AJs", "AQs", "AKs"]
 */
export function parseHandList(input: string): string[] {
  if (!input.trim()) {
    return [];
  }

  const hands = input.split(',');
  const validHands: string[] = [];
  const seen = new Set<string>();

  for (const hand of hands) {
    const expanded = expandHandExpression(hand);
    for (const h of expanded) {
      if (!seen.has(h)) {
        seen.add(h);
        validHands.push(h);
      }
    }
  }

  return validHands;
}

/**
 * Check if a string is a valid hand expression
 */
export function isValidHand(hand: string): boolean {
  return expandHandExpression(hand).length > 0;
}

/**
 * Get all valid hand names
 */
export function getAllHandNames(): string[] {
  return Object.keys(HAND_METADATA);
}
